import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateHranovePravidlo,
  useDeleteHranovePravidlo,
  useHranovePravidla,
} from "@/api/hooks/useHranovePravidla";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import type { HranaPravidlo, Material } from "@/api/schemas";

/**
 * Výnimky „pre tento dekor dosky použi túto hranu" (KOLO 121).
 *
 * User: *„Materiál 0375 javor priraďujem hranu 264375, lenže v cenníku je
 * preň priradená 261375… 261375 nemá hrúbku 1, ale 264375 má, je lepšie
 * dostupná."* Taká hrana sa do ponuky nedostane ani ako kandidát —
 * `EdgeAutoMapper` páruje podľa dekoru a 264375 patrí inému.
 *
 * Sekcia stojí hneď za „Používané hrúbky dosiek", lebo je to druhé
 * globálne hranové pravidlo a patria k sebe.
 */
export function HranovePravidlaSection(): JSX.Element {
  const query = useHranovePravidla();
  const deleteMutation = useDeleteHranovePravidlo();
  const [createOpen, setCreateOpen] = useState(false);

  const items = query.data?.data ?? [];

  async function handleDelete(p: HranaPravidlo): Promise<void> {
    if (!window.confirm(`Zmazať pravidlo ${p.dekor_kod} → ${p.hrana_rodina}?`))
      return;
    try {
      await deleteMutation.mutateAsync(p.id);
      toast({ title: "Pravidlo zmazané" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl">Hrany pre dekor</h2>
          <p className="text-xs text-muted-foreground">
            Výnimky z automatickej detekcie. Hrana, ktorú dodávateľ vedie
            pod <strong>iným</strong> dekorom, sa k tejto doske normálne
            nedostane ani do ponuky — tu ju pridáš a appka ju bude aj
            uprednostňovať. Platí <strong>globálne</strong>, vo všetkých
            zákazkách aj pri importe.
            <br />
            Pravidlo sa píše na <strong>celú rodinu hrany</strong> (všetky
            šírky a hrúbky), lebo dôvod výnimky je dostupnosť dekoru, nie
            konkrétny rozmer.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="sm">
          <Plus className="mr-1 h-4 w-4" /> Pridať
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dekor dosky</TableHead>
              <TableHead>Rodina hrany</TableHead>
              <TableHead>V cenníku</TableHead>
              <TableHead>Poznámka</TableHead>
              <TableHead className="w-16 text-right">Akcie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-6 text-center text-muted-foreground"
                >
                  Žiadne výnimky — hrany sa priraďujú len automaticky.
                </TableCell>
              </TableRow>
            ) : (
              items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono">{p.dekor_kod}</TableCell>
                  <TableCell className="font-mono">{p.hrana_rodina}</TableCell>
                  <TableCell
                    className={
                      p.pocet_hran === 0
                        ? "font-medium text-destructive"
                        : "text-muted-foreground"
                    }
                  >
                    {p.pocet_hran === 0 ? (
                      "nič — rodina v cenníku nie je"
                    ) : (
                      <span title={p.ukazka ?? undefined}>
                        {p.pocet_hran}{" "}
                        {p.pocet_hran === 1 ? "hrana" : "hrán"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.poznamka || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Zmazať pravidlo ${p.dekor_kod}`}
                      onClick={() => void handleDelete(p)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <HranovePravidloDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}

/**
 * Doska aj hrana sa **klikajú z cenníka**, nepíšu sa ručne.
 *
 * Posiela sa dvojica ID; dekor a rodinu z nich skladá backend. Keby to
 * robil tento komponent, bola by to druhá kópia normalizácie (dekor bez
 * štruktúry, hrana bez „ lep.") a rozišla by sa s tou, podľa ktorej sa
 * pravidlo číta.
 */
function HranovePravidloDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const createMutation = useCreateHranovePravidlo();
  const [doskaId, setDoskaId] = useState<string | null>(null);
  const [doska, setDoska] = useState<Material | null>(null);
  const [hranaId, setHranaId] = useState<string | null>(null);
  const [hrana, setHrana] = useState<Material | null>(null);
  const [poznamka, setPoznamka] = useState("");

  function reset(): void {
    setDoskaId(null);
    setDoska(null);
    setHranaId(null);
    setHrana(null);
    setPoznamka("");
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!doskaId || !hranaId) {
      toast({
        variant: "destructive",
        title: "Chýba výber",
        description: "Vyber dosku aj hranu z cenníka.",
      });
      return;
    }
    try {
      const r = await createMutation.mutateAsync({
        doska_material_id: doskaId,
        hrana_material_id: hranaId,
        poznamka: poznamka.trim() || null,
      });
      toast({
        title: "Pravidlo pridané",
        description: `${r.data.dekor_kod} → ${r.data.hrana_rodina}`,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Neuložené",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Pridať hranu pre dekor</DialogTitle>
          <DialogDescription>
            Vyber dosku a hranu z cenníka. Uloží sa dekor dosky bez
            štruktúry a celá rodina hrany (bez „lep." variantu) — presný
            tvar uvidíš po uložení v zozname.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Doska</Label>
            <MaterialSelect
              value={doskaId}
              onChange={(id, m) => {
                setDoskaId(id);
                setDoska(m ?? null);
              }}
              druh="plosny"
              sekcia="materialy"
              placeholder="Hľadaj dosku (0375, javor…)"
              kompaktny
            />
            {doska?.dekor_kod ? (
              <p className="text-xs text-muted-foreground">
                dekor v cenníku:{" "}
                <span className="font-mono">{doska.dekor_kod}</span>
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label>Hrana</Label>
            <MaterialSelect
              value={hranaId}
              onChange={(id, m) => {
                setHranaId(id);
                setHrana(m ?? null);
              }}
              druh="dlzkovy"
              sekcia="hrany"
              placeholder="Hľadaj hranu (264375…)"
              kompaktny
            />
            {hrana?.hranipex_id ? (
              <p className="text-xs text-muted-foreground">
                kód v cenníku:{" "}
                <span className="font-mono">{hrana.hranipex_id}</span>
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hp-poznamka">Prečo (voliteľné)</Label>
            <Input
              id="hp-poznamka"
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              maxLength={300}
              placeholder="261375 nemá hrúbku 1 mm, 264375 áno"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Zrušiť
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              Pridať
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
